import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  teams: [],
  no_teams: null,
  team_selected: '',
  setting_form: {
    name: '',
  },
  members: [],
  pending_invites: [],
  member_form: {
    mail: '',
    account_role: 2,
  },
  setting_result: {},
  create_form: {
    name: '',
  },
  create_result: {},
  delete_result: {},
  members_result: {},
  invite_result: {},
  member_role_result: {},
  member_delete_result: {},
  invitation_cancel_result: {},
  loading: false,
  error: null,
};

const teamsSlice = createSlice({
  name: 'teams',
  initialState,
  reducers: {
    fetchTeams: (state) => {
      state.loading = true;
      state.no_teams = null;
    },
    fetchTeamsSuccess: (state, action) => {
      const teams = action.payload.data;

      state.teams = teams;
      state.no_teams = teams.length === 0;

      state.team_selected = teams.length
        ? ((state.team_selected && teams.some((item) => item.token === state.team_selected))
          ? state.team_selected
          : teams[0].token)
        : '';

      state.setting_form.name = teams.length
        ? (teams.find((item) => item.token === state.team_selected)?.name || '')
        : '';

      if (!teams.length) {
        state.members = [];
        state.pending_invites = [];
      }

      state.loading = false;
    },
    fetchTeamsFailure: (state, action) => {
      state.error = action.payload;
      state.loading = false;
    },
    setTeamSelected: (state, action) => {
      state.team_selected = action.payload;
    },
    clearTeamsError: (state) => {
      state.error = null;
    },
    setSettingForm: (state, action) => {
      state.setting_form = action.payload;
    },
    teamSetting: (state) => {
      state.loading = true;
    },
    teamSettingSuccess: (state, action) => {
      state.setting_result = action.payload.data;
      state.loading = false;
    },
    teamSettingFailure: (state, action) => {
      state.error = action.payload;
      state.loading = false;
    },
    clearSettingResult: (state) => {
      state.setting_result = {};
    },
    setTeamsError: (state, action) => {
      state.error = action.payload;
    },
    teamDelete: (state) => {
      state.loading = true;
    },
    teamDeleteSuccess: (state, action) => {
      state.delete_result = action.payload.data;
      state.loading = false;
    },
    teamDeleteFailure: (state, action) => {
      state.error = action.payload;
      state.loading = false;
    },
    clearDeleteResult: (state) => {
      state.delete_result = {};
    },
    setCreateForm: (state, action) => {
      state.create_form = action.payload;
    },
    teamCreate: (state) => {
      state.loading = true;
    },
    teamCreateSuccess: (state, action) => {
      state.create_result = action.payload.data;
      state.loading = false;
    },
    teamCreateFailure: (state, action) => {
      state.error = action.payload;
      state.loading = false;
    },
    clearCreateResult: (state) => {
      state.create_result = {};
    },
    fetchTeamMembers: (state) => {
      state.loading = true;
    },
    fetchTeamMembersSuccess: (state, action) => {
      state.members = action.payload.data?.members || [];
      state.pending_invites = action.payload.data?.pending_invites || [];
      state.members_result = action.payload.data || {};
      state.loading = false;
    },
    fetchTeamMembersFailure: (state, action) => {
      state.error = action.payload;
      state.loading = false;
    },
    setMemberForm: (state, action) => {
      state.member_form = {
        ...state.member_form,
        ...action.payload,
      };
    },
    inviteTeamMember: (state) => {
      state.loading = true;
    },
    inviteTeamMemberSuccess: (state, action) => {
      state.invite_result = {
        ...(action.payload?.data || {}),
        msg: action.payload?.msg || '',
      };
      state.loading = false;
    },
    inviteTeamMemberFailure: (state, action) => {
      state.error = action.payload;
      state.loading = false;
    },
    clearInviteResult: (state) => {
      state.invite_result = {};
    },
    updateTeamMemberRole: (state) => {
      state.loading = true;
    },
    updateTeamMemberRoleSuccess: (state, action) => {
      state.member_role_result = action.payload.data || {};
      state.loading = false;
    },
    updateTeamMemberRoleFailure: (state, action) => {
      state.error = action.payload;
      state.loading = false;
    },
    clearMemberRoleResult: (state) => {
      state.member_role_result = {};
    },
    deleteTeamMember: (state) => {
      state.loading = true;
    },
    deleteTeamMemberSuccess: (state, action) => {
      state.member_delete_result = action.payload.data || {};
      state.loading = false;
    },
    deleteTeamMemberFailure: (state, action) => {
      state.error = action.payload;
      state.loading = false;
    },
    clearMemberDeleteResult: (state) => {
      state.member_delete_result = {};
    },
    cancelTeamInvitation: (state) => {
      state.loading = true;
    },
    cancelTeamInvitationSuccess: (state, action) => {
      state.invitation_cancel_result = action.payload.data || {};
      state.loading = false;
    },
    cancelTeamInvitationFailure: (state, action) => {
      state.error = action.payload;
      state.loading = false;
    },
    clearInvitationCancelResult: (state) => {
      state.invitation_cancel_result = {};
    },
  },
});

export const selectTeams = (state) => state.teams;

export const {
  fetchTeams,
  fetchTeamsSuccess,
  fetchTeamsFailure,
  setTeamSelected,
  clearTeamsError,
  setSettingForm,
  teamSetting,
  teamSettingSuccess,
  teamSettingFailure,
  clearSettingResult,
  setTeamsError,
  teamDelete,
  teamDeleteSuccess,
  teamDeleteFailure,
  clearDeleteResult,
  setCreateForm,
  teamCreate,
  teamCreateSuccess,
  teamCreateFailure,
  clearCreateResult,
  fetchTeamMembers,
  fetchTeamMembersSuccess,
  fetchTeamMembersFailure,
  setMemberForm,
  inviteTeamMember,
  inviteTeamMemberSuccess,
  inviteTeamMemberFailure,
  clearInviteResult,
  updateTeamMemberRole,
  updateTeamMemberRoleSuccess,
  updateTeamMemberRoleFailure,
  clearMemberRoleResult,
  deleteTeamMember,
  deleteTeamMemberSuccess,
  deleteTeamMemberFailure,
  clearMemberDeleteResult,
  cancelTeamInvitation,
  cancelTeamInvitationSuccess,
  cancelTeamInvitationFailure,
  clearInvitationCancelResult,
} = teamsSlice.actions;

export default teamsSlice.reducer;
